
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2 } from "lucide-react";
import { SubscriptionService } from "@/services/subscription";

export const useUpgradeHandler = (user: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleUpgradeClick = async (type: 'monthly' | 'yearly') => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`Initiating ${type} subscription checkout for user ${user.id}`);
      const url = await SubscriptionService.createCheckoutSession(user.id, type);
      
      if (url) {
        setCheckoutUrl(url);
        
        // Store the selected plan type in localStorage
        // This will be used to send the correct email after successful checkout
        const planName = type === 'monthly' ? 'Monthly' : 'Yearly';
        localStorage.setItem('selected_plan', planName);
        
        // Detect if we're in test mode based on URL
        const isTestMode = url.includes('checkout.stripe.com/c/pay/cs_test_');
        if (isTestMode) {
          localStorage.setItem('stripe_test_mode', 'true');
          console.log('Test mode subscription detected');
        }
        
        // Try to open the checkout in a new window
        console.log('Opening checkout URL:', url);
        const checkoutWindow = window.open(url, '_blank');
        
        if (!checkoutWindow) {
          toast({
            title: "Popup Blocked",
            description: "We couldn't open the checkout page automatically. Please click the button below to continue.",
            action: (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 w-full flex items-center justify-center gap-2"
                onClick={() => window.open(url, '_blank')}
              >
                Open Checkout <ExternalLink size={16} />
              </Button>
            ),
            duration: 10000,
          });
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
    
    return { checkoutUrl };
  };

  return { handleUpgradeClick, checkoutUrl, isLoading, setIsLoading };
};
